import {
  SPACING,
  FlexBox,
  JoonColorExpanded,
  Typography,
  TextArea,
} from "@joonapp/web-shared"
import { useEffect } from "react"

import useCoachOnboardingStore, {
  CoachOnboardingStep,
} from "./useCoachOnboardingStore"
import TextArrowButton from "../../components/coaching/TextArrowButton"
import LinearProgress from "../../components/linearProgress/LinearProgress"
import PageLoader from "../../components/loading/PageLoader"
import { useCoachOnboardIntakeFormQuestionnaireQuery } from "../../networking/queries"
import {
  AnswerInterface,
  QuestionAnswerMappingInterface,
  QuestionInterface,
  QuestionType,
} from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import CoachQuestionnaireAnswerButton from "../coaching/coachingFlow/CoachQuestionnaireAnswerButton"

function CoachQuestionnaireSection() {
  const { data: questionnaire, isLoading } =
    useCoachOnboardIntakeFormQuestionnaireQuery()
  const questions: QuestionInterface[] | [] = questionnaire?.questions || []
  const {
    coachQuestionnaireStep,
    setCoachQuestionnaireStep,
    questionAnswerMapping,
    setQuestionAnswerMapping,
    setStep,
    setPostAnswerScreenInfo,
    focus,
  } = useCoachOnboardingStore()

  const nextQuestion = (
    // checking for post answer screen
    answer?: AnswerInterface,
    // this is so that we have the most updated mapping for single choice questions
    updatedAnswers?: QuestionAnswerMappingInterface
  ) => {
    const currentQuestion = questions[coachQuestionnaireStep]
    if (coachQuestionnaireStep >= questions.length - 1) {
      // End of marketing-intake questionnaire — reverted to pre-strawberry
      // behavior (PR #878 funnel revert). Goes to COACH_SIGN_UP directly;
      // the PERSONALIZED_PLAN_REVEAL screen is no longer reached in the
      // default funnel. AddChildren is handled by the useEffect override
      // below for the multi-step path.
      setStep(CoachOnboardingStep.COACH_SIGN_UP)
      return
    }
    if (answer && answer.post_answer_screen) {
      setPostAnswerScreenInfo({
        background_color_hex: answer.post_answer_screen.background_color_hex,
        background_image_url:
          answer.post_answer_screen.background_image_url || "",
        button_title: answer.post_answer_screen.button_title,
      })
      setStep(CoachOnboardingStep.POST_ANSWER_SCREEN)
    }
    trackAnalyticEvent(
      ANALYTIC_EVENTS.MARKETING_INTAKE_FORM(Number(currentQuestion.id)),
      currentQuestion.type === QuestionType.FREEFORM
        ? {
            freeform_answer: updatedAnswers?.[currentQuestion.id],
            is_coach_onboarding: true,
            focus,
          }
        : {
            answers: updatedAnswers?.[currentQuestion.id],
            is_coach_onboarding: true,
            focus,
          }
    )
    setCoachQuestionnaireStep(coachQuestionnaireStep + 1)
  }

  const didSelectAnswer = (answer: AnswerInterface) => {
    // this is only called for single choice questions
    const currentQuestion = questions[coachQuestionnaireStep]
    const newMapping = {
      ...questionAnswerMapping,
      [currentQuestion.id]: [answer.id],
    }
    setQuestionAnswerMapping(newMapping)
    nextQuestion(answer, newMapping)
  }

  const toggleMultiSelectAnswer = (answer: AnswerInterface) => {
    const currentQuestion = questions[coachQuestionnaireStep]
    const currentAnswers = questionAnswerMapping[currentQuestion.id] || []
    if (typeof currentAnswers === "string") return
    const newAnswers = currentAnswers.includes(answer.id)
      ? currentAnswers.filter((id) => id !== answer.id)
      : [...currentAnswers, answer.id]
    const newMapping = {
      ...questionAnswerMapping,
      [currentQuestion.id]: newAnswers,
    }
    setQuestionAnswerMapping(newMapping)
  }

  const onSubmitMultiSelect = () => {
    // just chooses one answer to check for post answer screen
    const answer = currentQuestion.answers.find(
      (answer) => answer.id === questionAnswerMapping[currentQuestion.id]?.[0]
    )
    nextQuestion(answer, questionAnswerMapping)
  }

  const onSubmitFreeform = () => {
    // we do not have post answer screen for freeform questions
    nextQuestion(undefined, questionAnswerMapping)
  }

  useEffect(() => {
    if (questions.length > 0 && coachQuestionnaireStep >= questions.length) {
      trackAnalyticEvent(ANALYTIC_EVENTS.MARKETING_INTAKE_FORM_SUBMIT, {
        is_from_coach_onboarding: true,
        focus,
      })
      // End of questionnaire — reverted to pre-strawberry behavior
      // (PR #878 funnel revert). Children come after the questionnaire,
      // so the next stop is ADD_CHILDREN_START. This override also kicks
      // in when the last question's post_answer_screen would otherwise
      // pin us on the empathy screen.
      setStep(CoachOnboardingStep.ADD_CHILDREN_START)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachQuestionnaireStep, questionAnswerMapping])

  if (isLoading) return <PageLoader />

  const currentQuestion = questions[coachQuestionnaireStep]

  if (currentQuestion === undefined) return null

  return (
    <FlexBox
      direction="column"
      align="flex-start"
      justify="center"
      wrap={false}
      style={{
        width: "min(450px, 100%)",
        margin: "0 auto",
        height: "100%",
        paddingBottom: "10dvh",
      }}
    >
      <FlexBox
        direction="column"
        align="center"
        gap={SPACING.space6}
        fullWidth
        style={{
          minHeight: "65dvh",
          paddingTop: "5dvh",
        }}
      >
        <Typography
          variant="h3"
          textAlign="center"
          style={{ fontFamily: "Lora" }}
        >
          {currentQuestion.question}
        </Typography>
        {currentQuestion.type === QuestionType.SINGLE_CHOICE ? (
          <FlexBox
            direction="column"
            align="flex-start"
            gap={SPACING.space3}
            fullWidth
            wrap={false}
          >
            {currentQuestion.answers.map((answer) => (
              <CoachQuestionnaireAnswerButton
                key={answer.id}
                answer={answer}
                didSelectAnswer={didSelectAnswer}
                isMultiSelected={false}
                numberOfSelectedAnswers={
                  questionAnswerMapping[currentQuestion.id]?.length || 0
                }
                delay={true}
              />
            ))}
          </FlexBox>
        ) : currentQuestion.type === QuestionType.MULTI_CHOICE ? (
          <FlexBox direction="column" gap={SPACING.space4}>
            <FlexBox
              direction="column"
              align="flex-start"
              gap={SPACING.space3}
              fullWidth
            >
              <Typography variant="bodySmall" textAlign="center">
                Select up to 3
              </Typography>
              {currentQuestion.answers.map((answer) => (
                <CoachQuestionnaireAnswerButton
                  key={answer.id}
                  answer={answer}
                  didSelectAnswer={toggleMultiSelectAnswer}
                  isMultiSelected={(
                    questionAnswerMapping[currentQuestion.id] as number[]
                  )?.includes(answer.id)}
                  numberOfSelectedAnswers={
                    (questionAnswerMapping[currentQuestion.id] as number[])
                      ?.length || 0
                  }
                  delay={false}
                />
              ))}
            </FlexBox>
            <TextArrowButton
              text="Next"
              onClick={onSubmitMultiSelect}
              disabled={
                !questionAnswerMapping[currentQuestion.id] ||
                questionAnswerMapping[currentQuestion.id].length === 0
              }
              style={{
                width: "min(450px, calc(98% - 40px))",
                position: "fixed",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: "9dvh",
              }}
            />
          </FlexBox>
        ) : (
          <>
            <FlexBox
              direction="column"
              align="flex-start"
              gap={SPACING.space3}
              fullWidth
            >
              <TextArea
                placeholder="Type your answer..."
                value={
                  questionAnswerMapping[currentQuestion.id]?.toString() || ""
                }
                onChange={(e) =>
                  setQuestionAnswerMapping({
                    ...questionAnswerMapping,
                    [currentQuestion.id]: e.target.value,
                  })
                }
                name="freeformAnswer"
                maxLength={255}
                showCounter={true}
                fullWidth
              />
            </FlexBox>
            <TextArrowButton
              text="Next"
              onClick={onSubmitFreeform}
              disabled={
                !questionAnswerMapping[currentQuestion.id] ||
                questionAnswerMapping[currentQuestion.id]?.toString().length ===
                  0
              }
              style={{
                width: "min(450px, calc(98% - 40px))",
                position: "fixed",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: "9dvh",
              }}
            />
          </>
        )}
      </FlexBox>
      <p />
      <div
        style={{
          width: "450px",
          maxWidth: "calc(100% - 40px)",
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: "3vh",
        }}
      >
        <LinearProgress
          value={((coachQuestionnaireStep + 1) / questions.length) * 100}
          colorPrimary={JoonColorExpanded.viridian300}
          style={{
            width: "99%",
          }}
        />
      </div>
    </FlexBox>
  )
}

export default CoachQuestionnaireSection
